import {
  FormViewConfig,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  vsServiceName as ColumnDefinitionSetServiceName,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import { vsObjectName as displayName } from "./lookup/display";
import {
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../records/grids/cells/valueGetters";

export const vsObjectName = "machineConfigurationType";

machineConfigurationTypeConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];
export function machineConfigurationTypeConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider,
) {
  configureGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider,
  );

  configureForm(formViewServiceProvider);
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ColumnDefinitionSetServiceName,
    (columnDefinitionService: ColumnDefinitionService) => [
      columnDefinitionService.getNumberColDef({
        field: "Id",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineConfigurationTypeName",
        header: "Configuration Type",
      }),
      columnDefinitionService.getStringColDef({
        field: "Description",
      }),
      columnDefinitionService.getStringColDef({
        field: "DenomSpread",
        header: "Denom Spread",
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineDisplayName",
        header: "Display",
      }),
      columnDefinitionService.getStringColDef({
        field: "NumberOfReels",
        header: "Number of reels",
      }),
      columnDefinitionService.getNumberColDef({
        field: "Hopper",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiGame",
        header: "Multi Game?",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiDenom",
        header: "Multi Denomination?",
      }),
      columnDefinitionService.getNumberColDef({
        field: "DailyFee",
        header: "Daily Fee ($)",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "PartWin",
        header: "PartWin ($)",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Progressive",
      }),
      columnDefinitionService.getNumberColDef({
        field: "PartClPercentage",
        header: "PartCl %",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MinPercentage",
        header: "Min %",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxPercentage",
        header: "Max %",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "NetPercentage",
        header: "Net %",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxLiability",
        header: "Max Liability",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getFileColDef({
        field: "ProgressiveSetupForm",
        hide: true,
      }),
      columnDefinitionService.getFileColDef({
        field: "CoverSheet",
        hide: true,
      }),
      columnDefinitionService.getFileColDef({
        field: "ShipmentNotification",
        hide: true,
      }),
    ],
  ]);

  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    (
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService,
    ) => {
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: "MachineConfigurationTypeName",
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/machines/MachineConfigurationType",
        ),
        nameSingular: "Configuration Type",
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        showAddButton: true,
        showAddButtonPermission: "machineEdit",
      };
    },
  ]);
}
function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    (
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService,
    ): FormViewConfig => {
      return {
        fields: [
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "Name",
                required: true,
              }),
            ],
            4,
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: "Description",
                label: "Description",
                rows: 5,
              }),
            ],
            1,
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "DenomSpread",
                label: "Denom Spread",
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: "MachineDisplayId",
                  label: "Display",
                  objectName: displayName,
                },
              ),
              fieldHelpers.simpleStringField({
                key: "NumberOfReels",
                label: "Number of Reels",
              }),
              fieldHelpers.simplePercentageField({
                key: "Hopper",
                min: 0,
              }),
            ],
            4,
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: "MultiGame",
                label: "Multi Game?",
              }),
              fieldHelpers.simpleBoolField({
                key: "MultiDenom",
                label: "Multi Denomination?",
              }),
            ],
            4,
          ),
          fieldHelpers.headerField({
            headerText: "Participation / Action Fee",
            key: "ParticipationHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleCurrencyField({
                key: "DailyFee",
                label: "Daily Fee ($)",
              }),
              fieldHelpers.simpleCurrencyField({
                key: "PartWin",
                label: "PartWin ($)",
              }),
              fieldHelpers.simplePercentageField({
                key: "PartClPercentage",
                label: "PartCl %",
              }),
            ],
            4,
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simplePercentageField({
                key: "MinPercentage",
                label: "Min %",
              }),
              fieldHelpers.simplePercentageField({
                key: "MaxPercentage",
                label: "Max %",
              }),
              fieldHelpers.simpleCurrencyField({
                key: "MaxLiability",
                label: "Max Liability",
                min: 0,
              }),
            ],
            4,
          ),
          fieldHelpers.headerField({
            headerText: "Hyperlinks",
            key: "HyperlinksHeader",
          }),
          fieldConfigurationHelperService.repeaterField({
            allowAdd: true,
            allowAddBottom: false,
            displayNameSingular: "Hyperlink",
            displayNamePlural: "Hyperlinks",
            label: "Hyperlinks",
            key: "Hyperlinks",
            name: "Hyperlinks",
            repeaterFields: [
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "DisplayText",
                    name: "DisplayText",
                    label: "Display Text",
                    required: true,
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Url",
                    name: "Url",
                    label: "URL",
                    required: true,
                  }),
                  {
                    key: "copyDelete",
                    type: "recordRepeaterCopyDelete",
                    templateOptions: {
                      label: " ",
                      allowMove: false,
                    },
                  },
                ],
                4,
              ),
            ],
            showHeader: false,
          }),
          fieldHelpers.headerField({
            headerText: "Files",
            key: "FileHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleSingleFileUpload({
                key: "ProgressiveSetupForm",
                label: "Progressive Setup Form",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: "CoverSheet",
                label: "Cover Sheet",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: "ShipmentNotification",
                label: "Shipment Notification",
              }),
            ],
            4,
          ),
          fieldHelpers.headerField({
            key: "ParUploadsHeader",
            headerText: "PAR Sheet Uploads",
          }),
          {
            key: "Files",
            type: "recordFileUpload",
            templateOptions: {
              label: "Files",
              allowMultiple: true,
              hideLabel: true,
            },
          },
        ],
        name: vsObjectName,
        title: () => "Configuration Type",
        showButtonsAtTop: true,
      };
    },
  ]);
}
